// src/pages/KnowledgeBasePage.js
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { fmtDate, copyToClipboard } from '../utils/formatters';
import { getUser } from '../utils/auth';
import { API_BASE } from '../utils/api';
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  FileText,
  ChevronRight,
  X,
  Filter,
  ArrowLeft,
  Paperclip,
  BookOpen,
  Clock,
  User
} from 'lucide-react';

const SIDEBAR_NAV = 'var(--text-60a5fa)';
const BORDER_COLOR = 'var(--white-05)';
const BG_COLOR = 'transparent';
const PRIMARY_TEXT = 'var(--text-primary)';
const SECONDARY_TEXT = 'var(--text-muted)';
const CARD_BG = 'var(--ticket-card)';

const getFileExt = (filename) => (filename || '').split('.').pop().toUpperCase();

const CATEGORIES = ['All', 'Network', 'Hardware', 'Software', 'Access', 'Other'];

export default function KnowledgeBasePage({ showToast }) {
  const [articles, setArticles] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null); // The actual article object or 'new'
  const [filter, setFilter] = useState('All');
  const [sort, setSort] = useState('Latest Updated');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const { request, loading, backendUrl } = useApi();
  const user = getUser();
  const isIT = ['admin', 'it'].includes(user?.role);
  const navigate = useNavigate();

  // Form state for new/edit
  const [formData, setFormData] = useState({ issue_title: '', problem_summary: '', solution: '', category: 'Other' });
  const [attachments, setAttachments] = useState([]);

  const loadKb = useCallback(async (q = '') => {
    try {
      const data = await request(`/api/knowledge-base?q=${encodeURIComponent(q)}`);
      setArticles(data);
      setLoaded(true);
    } catch (err) {
      showToast(err.message, 'error');
    }
  }, [request, showToast]);

  useEffect(() => {
    loadKb('');
  }, [loadKb]);

  const handleSearch = (val) => {
    setSearch(val);
    loadKb(val);
  };

  const clearSearch = () => {
    setSearch('');
    loadKb('');
  };

  const filteredArticles = articles.filter(art => {
    if (filter === 'All') return true;
    return (art.category || 'Other') === filter;
  });

  const sortedArticles = [...filteredArticles].sort((a, b) => {
    if (sort === 'Latest Updated') return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
    if (sort === 'Oldest Updated') return new Date(a.updated_at || a.created_at) - new Date(b.updated_at || b.created_at);
    if (sort === 'A → Z') return (a.issue_title || '').localeCompare(b.issue_title || '');
    if (sort === 'Z → A') return (b.issue_title || '').localeCompare(a.issue_title || '');
    return 0;
  });

  const handleCreate = async () => {
    if (!formData.issue_title || !formData.solution) {
      return showToast('Title and Solution are required', 'error');
    }
    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([k, v]) => payload.append(k, v));
      attachments.forEach(f => payload.append('attachments', f));

      const token = localStorage.getItem('beo_token') || sessionStorage.getItem('beo_token');
      const res = await fetch(`${API_BASE}/api/knowledge-base`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: payload
      });

      if (!res.ok) throw new Error('Failed to create KB article');
      showToast('Article created successfully', 'success');
      setSelected(null);
      loadKb(search);
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const handleUpdate = async () => {
    if (!formData.issue_title || !formData.solution) {
      return showToast('Title and Solution are required', 'error');
    }
    try {
      // Note: The API might need adjustments for PUT with FormData if it doesn't support it
      // but for now we follow the existing pattern or assume it works similarly.
      // If the backend only supports POST for attachments, we might need a workaround.
      // Re-using the POST endpoint logic if the backend handles it, but typically it's PUT.
      const res = await request(`/api/knowledge-base/${selected.id}`, {
        method: 'PUT',
        body: formData
      });

      if (res.success) {
        showToast('Article updated successfully', 'success');
        setSelected(null);
        loadKb(search);
      }
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    try {
      const res = await request(`/api/knowledge-base/${id}`, { method: 'DELETE' });
      if (res.success) {
        showToast('Article deleted successfully', 'success');
        if (selected?.id === id) setSelected(null);
        setArticles(prev => prev.filter(a => a.id !== id));
      }
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const startEdit = (art) => {
    setFormData({
      issue_title: art.issue_title,
      problem_summary: art.problem_summary || '',
      solution: art.solution,
      category: art.category || 'Other'
    });
    setAttachments([]);
    setSelected({ ...art, isEditing: true });
  };

  const startNew = () => {
    setFormData({ issue_title: '', problem_summary: '', solution: '', category: 'Other' });
    setAttachments([]);
    setSelected('new');
  };

  // Rendering Components
  const renderHeader = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: PRIMARY_TEXT }}>Knowledge Base</h1>
        <p style={{ margin: '4px 0 0', fontSize: '14px', color: SECONDARY_TEXT }}>Find articles and solutions for common IT issues.</p>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        {/* Search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--white-30)' }} />
            <input
              type="text"
              placeholder="Search articles..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); handleSearch(e.target.value); }}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch(search)}
              style={{
                padding: '8px 36px 8px 38px',
                borderRadius: 10,
                border: `1px solid ${BORDER_COLOR}`,
                fontSize: 13,
                width: 280,
                height: 40,
                outline: 'none',
                background: 'var(--white-03)',
                color: PRIMARY_TEXT,
                transition: 'all 0.2s'
              }}
            />
            {search && (
              <button
                onClick={clearSearch}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, display: 'flex', color: SECONDARY_TEXT }}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Sort */}
        <div style={{ position: 'relative' }}>
          <button
            className="filter-chip"
            onClick={() => setShowSortDropdown(!showSortDropdown)}
            style={{ height: 40, display: 'flex', alignItems: 'center', gap: 8, background: 'var(--white-05)', border: `1px solid ${BORDER_COLOR}`, padding: '0 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: PRIMARY_TEXT }}
          >
            <Filter size={14} /> Sort: {sort}
          </button>
          {showSortDropdown && (
            <div style={{ position: 'absolute', top: 48, right: 0, background: 'var(--dark-modal)', border: `1px solid ${BORDER_COLOR}`, borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.3)', zIndex: 100, minWidth: 200, overflow: 'hidden', backdropFilter: 'blur(20px)' }}>
              {['Latest Updated', 'Oldest Updated', 'A → Z', 'Z → A'].map(s => (
                <div
                  key={s}
                  onClick={() => { setSort(s); setShowSortDropdown(false); }}
                  style={{ padding: '12px 18px', fontSize: 13, cursor: 'pointer', background: sort === s ? 'rgba(96, 165, 250, 0.1)' : 'transparent', color: sort === s ? 'var(--text-60a5fa)' : PRIMARY_TEXT, fontWeight: sort === s ? 700 : 500 }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--white-05)'}
                  onMouseLeave={e => e.currentTarget.style.background = sort === s ? 'rgba(96, 165, 250, 0.1)' : 'transparent'}
                >
                  {s}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create (Admin Only) */}
        {isIT && (
          <button
            onClick={startNew}
            className="search-btn"
            style={{ height: 40, padding: '0 20px', background: SIDEBAR_NAV, color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', boxShadow: '0 4px 15px rgba(96, 165, 250, 0.2)' }}
          >
            <Plus size={18} /> New Article
          </button>
        )}
      </div>
    </div>
  );

  const renderFilters = () => (
    <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
      {CATEGORIES.map(cat => (
        <button
          key={cat}
          onClick={() => setFilter(cat)}
          className={filter === cat ? 'filter-chip filter-chip-active' : 'filter-chip'}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            border: '1px solid transparent',
            transition: 'all 0.15s'
          }}
        >
          {cat}
        </button>
      ))}
    </div>
  );

  const renderArticleList = () => {
    if (loading && articles.length === 0) {
      return (
        <div style={{ padding: '40px 0', textAlign: 'center', color: SECONDARY_TEXT }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          Loading articles...
        </div>
      );
    }

    if (sortedArticles.length === 0) {
      return (
        <div style={{ padding: '100px 20px', textAlign: 'center', background: CARD_BG, borderRadius: 16, border: `1px solid ${BORDER_COLOR}`, backdropFilter: 'blur(16px)' }}>
          <BookOpen size={64} strokeWidth={1} style={{ color: 'var(--white-05)', marginBottom: 20 }} />
          <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: PRIMARY_TEXT }}>No articles found</h3>
          <p style={{ margin: '10px 0 0', color: SECONDARY_TEXT, fontSize: 14 }}>Try a different search or category</p>
        </div>
      );
    }

    return (
      <div style={{ background: CARD_BG, borderRadius: 16, border: `1px solid ${BORDER_COLOR}`, overflow: 'hidden', backdropFilter: 'blur(16px)' }}>
        {sortedArticles.map((art) => (
          <div
            key={art.id}
            className="kb-item"
            onClick={() => setSelected(art)}
            style={{
              padding: '20px 24px',
              borderBottom: `1px solid ${BORDER_COLOR}`,
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              transition: 'all 0.2s'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'var(--white-02)';
              const title = e.currentTarget.querySelector('.kb-title');
              if (title) title.style.color = 'var(--text-60a5fa)';
              if (isIT) {
                const editIcon = e.currentTarget.querySelector('.edit-overlay');
                if (editIcon) editIcon.style.opacity = '1';
              }
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'transparent';
              const title = e.currentTarget.querySelector('.kb-title');
              if (title) title.style.color = 'var(--text-d1d1d1)';
              if (isIT) {
                const editIcon = e.currentTarget.querySelector('.edit-overlay');
                if (editIcon) editIcon.style.opacity = '0';
              }
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div className="kb-title" style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-d1d1d1)', transition: 'color 0.2s' }}>
                {art.issue_title}
              </div>

              {isIT && (
                <div
                  className="edit-overlay"
                  style={{
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    display: 'flex',
                    gap: 10,
                    position: 'absolute',
                    right: 24,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(15, 15, 22, 0.9)',
                    padding: '8px 12px',
                    borderRadius: 10,
                    boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                    border: `1px solid ${BORDER_COLOR}`,
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); startEdit(art); }}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, color: SECONDARY_TEXT }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--text-60a5fa)'}
                    onMouseLeave={e => e.currentTarget.style.color = SECONDARY_TEXT}
                    title="Edit"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(art.id); }}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, color: '#F87171' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#EF4444'}
                    onMouseLeave={e => e.currentTarget.style.color = '#F87171'}
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>

            <p style={{ margin: '8px 0 16px', fontSize: 14, color: SECONDARY_TEXT, maxWidth: '85%', lineHeight: 1.5 }}>
              {art.problem_summary || 'No description available.'}
            </p>

            <div className="kb-meta" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span style={{ background: 'rgba(96, 165, 250, 0.1)', padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: 'var(--text-60a5fa)', border: '1px solid rgba(96, 165, 250, 0.2)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {art.category || 'Other'}
              </span>
              <span style={{ fontSize: 12, opacity: 0.6 }}>Updated {fmtDate(art.updated_at || art.created_at)}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderDetail = () => {
    if (!selected) return null;

    return (
      <div style={{ background: CARD_BG, borderRadius: 24, border: `1px solid ${BORDER_COLOR}`, padding: '40px 48px', boxShadow: '0 25px 50px -12px var(--black-50)', backdropFilter: 'blur(16px)' }}>
        <button
          onClick={() => setSelected(null)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, border: 'none', background: 'var(--white-05)', color: PRIMARY_TEXT, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 32, padding: '8px 16px', borderRadius: 10, border: `1px solid ${BORDER_COLOR}`, transition: 'all 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--white-06)'}
          onMouseLeave={e => e.currentTarget.style.background = 'var(--white-05)'}
        >
          <ArrowLeft size={16} /> Back to Articles
        </button>

        <div style={{ marginBottom: 40 }}>
          <span style={{ background: 'rgba(96, 165, 250, 0.1)', color: 'var(--text-60a5fa)', padding: '6px 16px', borderRadius: 20, fontSize: 12, fontWeight: 800, textTransform: 'uppercase', marginBottom: 16, display: 'inline-block', border: '1px solid rgba(96, 165, 250, 0.2)', letterSpacing: '0.05em' }}>
            {selected.category || 'Other'}
          </span>
          <h1 style={{ margin: 0, fontSize: '40px', fontWeight: 800, color: PRIMARY_TEXT, lineHeight: 1.1, letterSpacing: '-0.02em' }}>{selected.issue_title}</h1>
          <div style={{ marginTop: 20, display: 'flex', gap: 20, fontSize: 14, color: SECONDARY_TEXT, alignItems: 'center' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Clock size={14} /> Last updated {fmtDate(selected.updated_at || selected.created_at)}</span>
            <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--white-06)' }} />
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><User size={14} /> By {selected.created_by_username || 'IT Support'}</span>
          </div>
        </div>

        {selected.problem_summary && (
          <div style={{ marginBottom: 40, padding: 24, background: 'var(--white-03)', borderRadius: 16, border: `1px solid ${BORDER_COLOR}` }}>
            <h4 style={{ margin: '0 0 12px', fontSize: '11px', fontWeight: 800, color: 'var(--text-60a5fa)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Issue Summary</h4>
            <p style={{ margin: 0, fontSize: 16, color: PRIMARY_TEXT, lineHeight: 1.7, opacity: 0.9 }}>{selected.problem_summary}</p>
          </div>
        )}



        <div style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 800, color: 'var(--text-3b82f6)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Resolution Solution</h4>
            <button
              onClick={() => { copyToClipboard(selected.solution); showToast('Copied to clipboard', 'success'); }}
              style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)', color: 'var(--text-3b82f6)', padding: '8px 20px', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)'}
            >
              Copy Steps
            </button>
          </div>
          <div style={{ background: 'rgba(16, 185, 129, 0.03)', borderRadius: 20, padding: 32, border: '1px solid rgba(16, 185, 129, 0.15)', whiteSpace: 'pre-wrap', color: PRIMARY_TEXT, fontSize: 16, lineHeight: 1.8, opacity: 0.95 }}>
            {selected.solution}
          </div>
        </div>

        {selected.attachments?.length > 0 && (
          <div style={{ borderTop: `1px solid ${BORDER_COLOR}`, paddingTop: 24 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: '12px', fontWeight: 700, color: SECONDARY_TEXT, textTransform: 'uppercase' }}>Attachments</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {selected.attachments.map(a => (
                <a
                  key={a.id}
                  href={`${API_BASE}/api/attachments/${a.id}/download`}
                  download={a.file_name}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 8, border: `1px solid ${BORDER_COLOR}`, textDecoration: 'none', color: PRIMARY_TEXT, background: 'var(--surface)', fontSize: 13 }}
                >
                  <Paperclip size={14} style={{ color: SECONDARY_TEXT }} />
                  {a.file_name} <span style={{ fontSize: 10, color: SECONDARY_TEXT, fontWeight: 700 }}>({getFileExt(a.file_name)})</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {isIT && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 40, paddingTop: 24, borderTop: `1px solid ${BORDER_COLOR}` }}>
            <button onClick={() => handleDelete(selected.id)} style={{ padding: '0 16px', height: 38, borderRadius: 6, border: '1px solid #FECACA', color: '#DC2626', background: 'var(--surface)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
            <button onClick={() => startEdit(selected)} style={{ padding: '0 16px', height: 38, borderRadius: 6, border: `1px solid ${BORDER_COLOR}`, color: PRIMARY_TEXT, background: 'var(--surface)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Edit Article</button>
          </div>
        )}
      </div>
    );
  };

  const renderForm = () => {
    const isEdit = selected !== 'new' && selected?.isEditing;
    return (
      <div className="form-container" style={{ margin: '0 auto', background: 'var(--surface)', borderRadius: 12, border: `1px solid ${BORDER_COLOR}`, overflow: 'hidden' }}>
        <div className="form-wrapper">
          <div className="form-header">
            <button
              onClick={() => setSelected(null)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent', color: SECONDARY_TEXT, fontSize: 13, cursor: 'pointer', padding: 0 }}
            >
              <ArrowLeft size={16} /> Back
            </button>
            <h2 className="form-title" style={{ margin: 0 }}>{isEdit ? 'Edit Article' : 'Create Knowledge Article'}</h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Issue Title *</label>
                <input
                  className="form-control"
                  value={formData.issue_title}
                  onChange={e => setFormData({ ...formData, issue_title: e.target.value })}
                  placeholder="e.g. How to connect to corporate VPN"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Category</label>
                <select
                  className="form-control"
                  value={formData.category}
                  onChange={e => setFormData({ ...formData, category: e.target.value })}
                  style={{ background: 'var(--surface)' }}
                >
                  {CATEGORIES.filter(c => c !== 'All').map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Summary (Optional)</label>
              <textarea
                className="form-control"
                rows={3}
                value={formData.problem_summary}
                onChange={e => setFormData({ ...formData, problem_summary: e.target.value })}
                placeholder="Brief description of the issue..."
              />
            </div>

            <div className="form-group">
              <label className="form-label">Solution / Steps *</label>
              <textarea
                className="form-control solution-box"
                value={formData.solution}
                onChange={e => setFormData({ ...formData, solution: e.target.value })}
                placeholder="Enter detailed step-by-step instructions..."
              />
            </div>

            {!isEdit && (
              <div className="form-group">
                <label className="form-label">Attachments</label>
                <div style={{ fontSize: '13px', color: SECONDARY_TEXT, marginBottom: 8 }}>
                  {attachments.length > 0 ? attachments.map(f => f.name).join(', ') : 'No files selected'}
                </div>
                <input
                  type="file"
                  multiple
                  onChange={e => setAttachments(Array.from(e.target.files))}
                  style={{ fontSize: '13px', color: SECONDARY_TEXT }}
                />
              </div>
            )}

            <div className="form-actions">
              <button
                onClick={() => setSelected(null)}
                className="btn-outline-subtle"
              >
                Cancel
              </button>
              <button
                onClick={isEdit ? handleUpdate : handleCreate}
                className="btn-primary-saas"
              >
                {isEdit ? 'Update Article' : 'Save Article'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="kb-container" style={{ padding: '32px', background: BG_COLOR, minHeight: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        .kb-item { 
          padding: 20px 24px; 
          border-bottom: 1px solid ${BORDER_COLOR}; 
          cursor: pointer; 
          transition: all 0.2s;
        }
        .kb-item:hover { background: var(--white-02); }
        .kb-title { font-weight: 700; color: ${PRIMARY_TEXT}; }
        .kb-meta { font-size: 12px; opacity: 0.6; display: flex; gap: 16px; margin-top: 12px; color: ${SECONDARY_TEXT}; }
        .filter-chip { 
          display: inline-flex; 
          align-items: center; 
          padding: 8px 18px; 
          border-radius: 10px; 
          font-size: 13px; 
          font-weight: 600; 
          background: var(--white-05); 
          color: ${SECONDARY_TEXT}; 
          border: 1px solid ${BORDER_COLOR}; 
          cursor: pointer; 
          transition: all 0.2s; 
          white-space: nowrap; 
        }
        .filter-chip:hover { background: var(--white-06); color: ${PRIMARY_TEXT}; border-color: var(--white-20); }
        .filter-chip-active { background: var(--text-60a5fa) !important; color: #FFFFFF !important; border-color: var(--text-60a5fa) !important; boxShadow: 0 4px 12px rgba(96, 165, 250, 0.3); }
        
        /* Form Specific Styles */
        .form-container { 
          max-width: 900px; 
          width: 100%; 
          background: ${CARD_BG} !important;
          backdrop-filter: blur(20px);
        }
        .form-wrapper { 
          padding: 32px 40px; 
        }
        .form-header { 
          display: flex; 
          align-items: center; 
          gap: 16px; 
          margin-bottom: 32px; 
        }
        .form-title { 
          font-size: 26px; 
          font-weight: 800; 
          color: ${PRIMARY_TEXT}; 
          letterSpacing: -0.02em;
        }
        .form-group { 
          display: flex; 
          flex-direction: column; 
          gap: 10px; 
          margin-bottom: 24px; 
        }
        .form-label { 
          font-size: 13px; 
          font-weight: 700; 
          color: ${SECONDARY_TEXT}; 
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .form-row { 
          display: grid; 
          grid-template-columns: 1fr 1fr; 
          gap: 20px; 
        }
        .form-control { 
          height: 48px; 
          padding: 0 16px; 
          border-radius: 12px; 
          border: 1px solid ${BORDER_COLOR}; 
          background: var(--black-30);
          color: ${PRIMARY_TEXT};
          font-size: 15px; 
          outline: none; 
          font-family: inherit;
          transition: all 0.2s;
        }
        textarea.form-control { 
          height: auto; 
          min-height: 140px; 
          padding: 16px; 
        }
        .solution-box { 
          min-height: 200px; 
        }
        .form-actions { 
          display: flex; 
          justify-content: flex-end; 
          gap: 12px; 
          margin-top: 32px; 
        }
        .btn-primary-saas { 
          background: var(--text-60a5fa); 
          color: #fff; 
          border: none; 
          border-radius: 12px; 
          padding: 0 24px; 
          height: 48px; 
          font-size: 14px; 
          font-weight: 700; 
          cursor: pointer; 
          transition: all 0.2s;
          boxShadow: 0 4px 15px rgba(96, 165, 250, 0.3);
        }
        .btn-primary-saas:hover { transform: translateY(-2px); opacity: 0.9; }
        .btn-outline-subtle { 
          background: var(--white-05); 
          border: 1px solid ${BORDER_COLOR}; 
          border-radius: 12px; 
          padding: 0 24px; 
          height: 48px; 
          font-size: 14px; 
          font-weight: 700; 
          color: ${PRIMARY_TEXT}; 
          cursor: pointer; 
          transition: all 0.2s;
        }
        .btn-outline-subtle:hover { background: var(--white-06); border-color: var(--white-20); }
        
        .form-control:focus { border-color: var(--text-60a5fa) !important; background: var(--black-40); }
        .spinner { width: 32px; height: 32px; border: 4px solid rgba(96, 165, 250, 0.1); border-top-color: var(--text-60a5fa); border-radius: 50%; animation: spin 0.8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        
        @media (max-width: 640px) {
          .form-row { grid-template-columns: 1fr; }
          .form-wrapper { padding: 20px; }
        }
      `}</style>

      {!selected ? (
        <>
          {renderHeader()}
          {renderFilters()}

          {renderArticleList()}
        </>
      ) : selected === 'new' || selected.isEditing ? (
        renderForm()
      ) : (
        renderDetail()
      )}
    </div>
  );
}
